import cn from "classnames";
import { marked } from "marked";
import { FC, useEffect, useMemo } from "react";

import { isEmpty } from "@app/shared/lib/is-empty";

import styles from "./MarkdownText.module.scss";

type Props = {
    text: string;
    // TODO describe settings type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    settings?: any;
    className?: string;
    linkClassName?: string;
    ellipsis?: boolean;
    minimal?: boolean;
    placeholder?: React.ReactNode;
    onParsed?: (x: string) => void;
};

export const MarkdownText: FC<Props> = ({
    text,
    settings,
    className,
    linkClassName = "break-all",
    ellipsis,
    minimal,
    placeholder,
    onParsed,
}) => {
    const modifiedText = useMemo(() => {
        if (isEmpty(text)) return "";
        const lines = text.split("\n");

        // check all lines, if it's empty line, make a line break, if not, return the line
        return lines
            .map((line) => {
                if (line.trim().length === 0) return `${line}&nbsp;\n`;

                return line;
            })
            .join("\n");
    }, [text]);

    const textHtml = useMemo(() => {
        const renderer = new marked.Renderer();

        renderer.paragraph = (pText: string) => {
            return `<p class="mb-0 mt-0">${pText}</p>`;
        };

        renderer.link = (href: string | null, _title: string | null, linkText: string) => {
            return `<a href="${href}" target="_blank" class="${linkClassName}">${linkText}</a>`;
        };

        if (minimal)
            renderer.image = () => "";

        const options: any = {
            breaks: true,
            gfm: true,
            renderer,
            ...settings,
        };

        if (minimal) {
            return marked.parseInline(modifiedText, options);
        }

        return marked.parse(modifiedText, options) as any;
    }, [modifiedText, minimal, settings, linkClassName]);

    useEffect(() => {
        onParsed?.(textHtml);
    }, [textHtml, onParsed]);

    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (isEmpty(textHtml)) return <>{placeholder}</>;

    return (
        <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: textHtml }}
            className={cn(className, styles.markdown_inner)}
            data-ellipsis={ellipsis}
        />
    );
};
